// src/components/Dashboard/DashboardTableHeadCell.tsx
//
// Shared header cell for the Outsourcing dashboard tables (EE-238). The two
// tables previously rendered their headers at different sizes (Inquiry at
// 1.1em/body1, Requisition at body2) and only the Requisition one styled the
// TableSortLabel's arrow white, so sorted columns looked different on each
// page. This settles on the larger Inquiry sizing for both.
//
// The cell pins its own `padding="normal"`. MUI passes `Table`'s `padding`
// down through context, so without this an ancestor `<Table padding="none">`
// silently renders this component at zero padding on one page and 16px on the
// other - which is exactly how the two headers drifted apart before.
import { FunctionComponent, ReactNode } from "react";
import { IconButton, TableCell, TableSortLabel, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { TableCellProps } from "@mui/material/TableCell";

type PropsType = {
  children: ReactNode;
  align?: TableCellProps["align"];
  // Sorting is opt-in: omit onSort to render a plain, non-interactive header.
  onSort?: () => void;
  sortActive?: boolean;
  sortDirection?: "asc" | "desc";
  disabled?: boolean;
};

const DashboardTableHeadCell: FunctionComponent<PropsType> = ({
  children,
  align = "left",
  onSort,
  sortActive = false,
  sortDirection = "desc",
  disabled = false,
}) => {
  const label = (
    <Typography
      fontSize="1.1em"
      color="white"
      fontWeight="bold"
      variant="body1"
    >
      {children}
    </Typography>
  );

  return (
    <TableCell align={align} padding="normal">
      {onSort ? (
        <TableSortLabel
          active={sortActive}
          direction={sortDirection}
          onClick={onSort}
          disabled={disabled}
          sx={{
            color: "white !important",
            "& .MuiTableSortLabel-icon": { color: "white !important" },
          }}
        >
          {label}
        </TableSortLabel>
      ) : (
        label
      )}
    </TableCell>
  );
};

export default DashboardTableHeadCell;

// Trailing header cell holding the table's refresh control. Both dashboards
// used to hand-roll this as a raw TableCell + IconButton, which is how the
// Inquiry one ended up inheriting `padding: 0` and staying clickable during a
// fetch while the Requisition one disabled itself (EE-238).
export const DashboardTableRefreshCell: FunctionComponent<{
  onRefresh: () => void;
  disabled?: boolean;
}> = ({ onRefresh, disabled = false }) => (
  <TableCell align="right" size="small" padding="normal">
    <IconButton
      sx={{ color: "var(--white)" }}
      onClick={onRefresh}
      disabled={disabled}
    >
      <Refresh />
    </IconButton>
  </TableCell>
);
